import { ENUM_VALUE_HELP, ENUM_VALUE_HELP_ENTITIES, ERROR_CODE_HELP, RULE_HELP } from './helpRefs.generated';
import { helpPanel, parseHelpLink } from './helpPanelStore';

/**
 * The help sections the DSL's `helpRef` names (docs/15-designspecs/User-Help-And-Setup-Guides.md §Rule and
 * enum-value references), looked up for what the UI shows: a refusal by its error code or the rules that
 * failed, an enum value by its type and code. Each answer is a section id, `<page id>#<anchor>`.
 */

/** The part of an ABP error envelope a refusal's help is read from. */
export interface RefusalEnvelope {
  code?: unknown;
  data?: { ruleKeys?: unknown } | null;
}

/** The code every rule-engine refusal carries (`Nexo:{Module}:{Entity}:RuleValidationFailed`, or the bare legacy one). */
const RULE_REFUSAL_SUFFIX = 'RuleValidationFailed';

function sectionOf(map: Record<string, string>, key: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;
}

/** The failed rules' keys a refusal names in `data.ruleKeys`: a comma-separated string or a list. */
function ruleKeysOf(ruleKeys: unknown): string[] {
  if (Array.isArray(ruleKeys)) return ruleKeys.filter((key): key is string => typeof key === 'string');
  if (typeof ruleKeys !== 'string') return [];
  return ruleKeys.split(',').map((key) => key.trim()).filter(Boolean);
}

function firstRuleSection(ruleKeys: unknown): string | undefined {
  return ruleKeysOf(ruleKeys).map((key) => sectionOf(RULE_HELP, key)).find(Boolean);
}

/**
 * The section that explains a refusal: its error code's, else — for a rule-engine refusal, whose one code
 * is shared by every rule — the first failed rule's that names one. Other codes that list rule keys (a rule
 * that could not be evaluated, warnings awaiting acknowledgement) are not about what the rule says.
 */
export function refusalHelpRef(envelope: RefusalEnvelope | null | undefined): string | undefined {
  const code = envelope?.code;
  if (typeof code !== 'string' || code === '') return undefined;
  const byCode = sectionOf(ERROR_CODE_HELP, code);
  if (byCode || !code.endsWith(RULE_REFUSAL_SUFFIX)) return byCode;
  return firstRuleSection(envelope?.data?.ruleKeys);
}

/**
 * The section that explains one value of an enum, a pending reason say, on a screen of `entity` — the
 * entity whose field holds the value. An enum is shared by entities, and its help page explains it for the
 * entities the page applies to only: a budget line's pending reason is no explanation of a pre-calculation
 * line's. Elsewhere there is no section.
 */
export function enumValueHelpRef(enumType: string | undefined, value: unknown, entity: string | undefined): string | undefined {
  if (!enumType || !entity || typeof value !== 'string' || value === '') return undefined;
  const key = `${enumType}:${value}`;
  return ENUM_VALUE_HELP_ENTITIES[key]?.includes(entity) ? sectionOf(ENUM_VALUE_HELP, key) : undefined;
}

/** Opens the help panel at a section, from outside the panel. */
export function openHelpSection(sectionId: string): void {
  const target = parseHelpLink(`help:${sectionId}`);
  if (target) helpPanel.openAt(target);
}
